/**
 * PanelPlanning map features mixin — 지도에 패널(String/Module/Array) 그리기
 *
 * Thin wrapper: 순수 로직은 panelMapFeaturesService.js 로 분리.
 * Vue 의존성(this.vectorSource, this.map, this.flattenedSiteDesign 등)만 여기서 처리.
 */
import { getDataByLevel } from '../../../utils/siteDesignDataFlattener.js';
import { PANEL_MAP_PAN_DURATION_MS } from '../utils/panelMapMotion.js';
import { drawStringFeature, drawModuleFeature } from '../utils/featureDrawingAdapter.js';
import {
  getShapeGeogPolygonRing,
  convToWGS84,
  convToFeatureWithHierarchy,
  computeModuleDimensions,
  findBestReferenceString
} from '../services/panelMapFeaturesService.js';
import { readStringSeq } from '../utils/panelMapFeatureProps.js';
import {
  panelPerfSync,
  panelPerfCountSummary,
  panelPerfLogTileStats,
  panelPerfMark,
  panelPerfMeasure
} from '../utils/panelPlanningPerfLog.js';

export const featureSetup = {
  methods: {
    // ────────────────────────────────────────────────────────
    //  순수 함수 델리게이트
    // ────────────────────────────────────────────────────────

    convToFeatureWithHierarchy(id, name, coordinates, style, hierarchy) {
      return convToFeatureWithHierarchy(id, name, coordinates, style, hierarchy);
    },

    convToWGS84(coordinates) {
      return convToWGS84(coordinates);
    },

    // ────────────────────────────────────────────────────────
    //  Vue 의존 메서드 (this.* 사용)
    // ────────────────────────────────────────────────────────

    drawSiteDesignOnMap(site, options = {}) {
      if (!this.vectorSource) {
        panelPerfCountSummary('drawSiteDesignOnMap:skip', { reason: 'no vectorSource' });
        return;
      }

      panelPerfSync('drawSiteDesignOnMap', () => {
        if (options?.preserveStringFeatures) {
        const isStringFeature = f => {
          const ft = f.get('featureType');
          const name = f.get('name') || '';
          return ft === 'String' || ft === 'string' || name.startsWith('String');
        };
        const isModuleFeature = f => {
          const ft = f.get('featureType');
          const name = f.get('name') || '';
          return ft === 'Module' || ft === 'module' || name.startsWith('Module');
        };
        const existingStringFeatures = this.vectorSource.getFeatures().filter(isStringFeature);
        this.vectorSource
          .getFeatures()
          .filter(isModuleFeature)
          .forEach(f => this.vectorSource.removeFeature(f));

        if (this.flattenedSiteDesign) {
          this.updateModulesFromFlattenedData();
        } else {
          this.processSolarSections(site);
        }

        existingStringFeatures.forEach(f => this.vectorSource.addFeature(f));
        this._cachedMapFeatureIndex = null;
        this.syncModuleLabelVisibilityAfterFeaturesChange?.();
        return;
      }

      this.solarStrings = [];
      this.solarStringIndex = 0;
      this.moduleWidth = null;
      this.moduleHeight = null;

      this.processSolarSections(site);

      if (options?.skipAnimation) {
        this.map?.render();
      }

      this.drawnPolygon = null;
      this.moduleColNum = 2;
      this.moduleRowNum = 2;
      this._cachedMapFeatureIndex = null;

      this.initializeMapDrawingManager?.();

      this.syncModuleLabelVisibilityAfterFeaturesChange?.();
      this.requestVectorStyleRefresh?.();

      if (this.solarStrings?.length) {
        panelPerfMark('drawSiteDesignOnMap:entityInit:start');
        this.initializeEntityManager?.();
        panelPerfMeasure('drawSiteDesignOnMap:entityInit', 'drawSiteDesignOnMap:entityInit:start');
      }

      if (!options?.skipFit) {
        panelPerfMark('fitMapToPanelDesignExtent:start');
        this.fitMapToPanelDesignExtent?.();
        panelPerfLogTileStats('after fitMapToPanelDesignExtent');
      }
      });
    },

    fitMapToPanelDesignExtent() {
      const features = this.vectorSource?.getFeatures?.() ?? [];
      if (!features.length || typeof this.panToFeaturesExtent !== 'function') {
        return;
      }
      this.panToFeaturesExtent(features, { duration: PANEL_MAP_PAN_DURATION_MS, maxZoom: 21 });
    },

    /**
     * 첫 번째 String shapeGeog·rowNum·colNum 으로 모듈 1칸 크기(moduleWidth/Height) 산출
     * → processDrawnPolygon 에서 행·열 초기값 자동 계산에 사용
     */
    calculateModuleDimensions(solarString) {
      const dims = computeModuleDimensions(solarString);
      if (!dims) return;

      if (dims.moduleWidth !== undefined) {
        this.moduleWidth = dims.moduleWidth;
      }
      if (dims.moduleHeight !== undefined) {
        this.moduleHeight = dims.moduleHeight;
      }
    },

    /** rowNum/colNum 이 있는 String 중 stringId가 가장 작은(첫) 것으로 1칸 크기 갱신 */
    updateReferenceModuleDimensionsFromStrings(strings) {
      const best = findBestReferenceString(strings);
      if (best) {
        this.calculateModuleDimensions(best);
      }
    },

    processSolarSections(site) {
      if (!this.vectorSource) {
        console.warn('⚠️ processSolarSections: vectorSource 없음');
        return;
      }
      const featureCountBefore = this.vectorSource.getFeatures().length;
      panelPerfMark('processSolarSections:start', {
        flattened: !!this.flattenedSiteDesign,
        featureCountBefore
      });

      this.clearMapStyleCaches?.();
      this.vectorSource.clear();
      this.solarStrings = [];
      this.solarStringIndex = 0;

      if (this.flattenedSiteDesign) {
        panelPerfMark('processSolarSections:flattenedDraw:start');
        this.processSolarSectionsWithFlattenedData();
        panelPerfMeasure('processSolarSections:flattenedDraw', 'processSolarSections:flattenedDraw:start');
      } else if (site?.solarSections) {
        site.solarSections.forEach((section, si) => {
          section?.solarArrays?.forEach((solarArray, ai) => {
            solarArray?.solarStrings?.forEach(solarString => {
              this.solarStringIndex += 1;
              this.drawStringPolygon(solarString, solarArray, section, si, ai);
              this.drawModulePolygons(solarString, solarArray, section, si, ai);
              this.solarStrings.push(solarString);
            });
          });
        });
        this.updateReferenceModuleDimensionsFromStrings(this.solarStrings);
        this._cachedMapFeatureIndex = null;
      }

      panelPerfCountSummary('processSolarSections:done', {
        strings: this.solarStrings?.length ?? 0,
        features: this.vectorSource.getFeatures().length
      });
      this.refreshPointerMovePerfFeatureCounts?.();
      panelPerfMeasure('processSolarSections', 'processSolarSections:start');
    },

    processSolarSectionsWithFlattenedData() {
      if (!this.flattenedSiteDesign) {
        return;
      }
      const strings = getDataByLevel(this.flattenedSiteDesign, 'string') || [];
      panelPerfCountSummary('processSolarSections:flattenedDraw:counts', {
        strings: strings.length
      });
      let stringDrawMs = 0;
      let moduleDrawMs = 0;
      strings.forEach(stringInfo => {
        this.solarStringIndex += 1;
        const arrayInfo = this.flattenedSiteDesign.byArray?.get?.(stringInfo.parentId);
        const sectionInfo = this.flattenedSiteDesign.bySection?.get?.(arrayInfo?.parentId);
        const t0 = performance.now();
        this.drawStringPolygonWithFlattenedData(stringInfo, arrayInfo, sectionInfo);
        stringDrawMs += performance.now() - t0;
        const t1 = performance.now();
        this.drawModulePolygonsWithFlattenedData(stringInfo, arrayInfo, sectionInfo);
        moduleDrawMs += performance.now() - t1;
        this.solarStrings.push({
          ...stringInfo,
          stringId: stringInfo.stringId,
          solarArrayId: arrayInfo?.id,
          solarModules: stringInfo.solarModules || []
        });
      });
      panelPerfCountSummary('processSolarSections:flattenedDraw:timing', {
        stringDrawMs: stringDrawMs.toFixed(1),
        moduleDrawMs: moduleDrawMs.toFixed(1)
      });
      this.updateReferenceModuleDimensionsFromStrings(this.solarStrings);
      this._cachedMapFeatureIndex = null;
    },

    drawStringPolygonWithFlattenedData(stringInfo, arrayInfo, sectionInfo) {
      drawStringFeature(this.vectorSource, stringInfo, arrayInfo, sectionInfo);
    },

    drawModulePolygonsWithFlattenedData(stringInfo, arrayInfo, sectionInfo) {
      stringInfo?.solarModules?.forEach(module => {
        drawModuleFeature(this.vectorSource, module, stringInfo, sectionInfo, arrayInfo);
      });
    },

    drawStringPolygon(solarString, solarArray, section, sectionIndex, arrayIndex) {
      drawStringFeature(
        this.vectorSource,
        {
          stringId: solarString.stringId,
          id: solarString.id,
          shapeGeog: solarString.shapeGeog,
          sectionIndex,
          arrayIndex
        },
        { id: solarArray?.id || arrayIndex },
        { id: section?.id || sectionIndex }
      );
    },

    drawModulePolygons(solarString, solarArray, section, sectionIndex, arrayIndex) {
      solarString?.solarModules?.forEach(module => {
        drawModuleFeature(
          this.vectorSource,
          {
            moduleId: module.moduleId,
            shapeGeog: module.shapeGeog
          },
          {
            stringId: solarString.stringId,
            id: solarString.id,
            name: solarString.name,
            shapeGeog: solarString.shapeGeog,
            rowNum: solarString.rowNum,
            colNum: solarString.colNum,
            sectionIndex,
            arrayIndex
          },
          { id: section?.id || sectionIndex },
          { id: solarArray?.id || arrayIndex }
        );
      });
    },

    drawArrayPolygon(solarArray, section, sectionIndex, arrayIndex) {
      const allCoords = [];
      solarArray?.solarStrings?.forEach(ss => {
        const ring = getShapeGeogPolygonRing(ss?.shapeGeog);
        if (ring) {
          allCoords.push(...ring);
        }
      });
      if (allCoords.length < 3) {
        return;
      }
      try {
        const feat = this.convToFeatureWithHierarchy(
          solarArray.id || arrayIndex,
          `Array ${solarArray.arrayId || arrayIndex}`,
          allCoords,
          null,
          {
            solarSectionId: section?.id || sectionIndex,
            solarArrayId: solarArray?.id || arrayIndex,
            featureType: 'Array'
          }
        );
        this.vectorSource.addFeature(feat);
      } catch (error) {
        console.error('❌ Array feature 생성 실패:', error);
      }
    },

    updateModulesFromFlattenedData() {
      if (!this.flattenedSiteDesign) {
        return;
      }
      const existingStrings = new Set();
      this.vectorSource.getFeatures().forEach(f => {
        const ft = f.get('featureType');
        if (ft === 'String' || ft === 'string') {
          const seq = readStringSeq(f);
          if (seq) existingStrings.add(seq);
        }
      });

      const allModules = getDataByLevel(this.flattenedSiteDesign, 'module') || [];
      allModules.forEach(moduleInfo => {
        const sid = moduleInfo.parentId;
        if (!existingStrings.has(String(sid))) {
          return;
        }
        const already = this.vectorSource
          .getFeatures()
          .find(
            f =>
              f.get('featureType') === 'Module' &&
              String(f.get('solarModuleId')) === String(moduleInfo.moduleId)
          );
        if (already) {
          return;
        }
        const ring = getShapeGeogPolygonRing(moduleInfo?.shapeGeog);
        if (!ring) {
          return;
        }
        try {
          const feat = this.convToFeatureWithHierarchy(
            moduleInfo.id,
            `Module ${moduleInfo.moduleId}`,
            ring,
            null,
            {
              solarStringId: moduleInfo.stringId || sid,
              solarModuleId: moduleInfo.moduleId,
              featureType: 'Module'
            }
          );
          this.vectorSource.addFeature(feat);
        } catch (error) {
          console.error('❌ Module feature 갱신 실패:', moduleInfo.moduleId, error);
        }
      });
    }
  }
};
